/**
 * TrafficPage — "Tráfico Web".
 *
 * Overview oficial de Google Analytics 4 (Data API): KPIs de audiencia, serie de
 * sesiones, canales de adquisición, páginas top, dispositivos y países. Se
 * alimenta de /api/ga/traffic. Requiere que un admin haya conectado GA4 en
 * "Integraciones".
 *
 * Rango de fechas: presets (7 / 28 / 90 días · 12 meses) + personalizado. El rango
 * viaja como date_from/date_to al backend, que lo aplica a TODOS los reportes GA4
 * (fetch_ga_traffic), así que cada KPI y serie se recalcula del lado del servidor.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
  ArcElement, Tooltip, Legend, Filler,
} from 'chart.js';
import {
  FaGlobe, FaSync, FaSpinner, FaPlug, FaExclamationTriangle, FaUsers, FaEye,
  FaClock, FaBullseye, FaChartLine, FaMousePointer, FaRegCalendarAlt,
  FaMobileAlt, FaDesktop, FaTabletAlt, FaArrowUp, FaArrowDown, FaChartBar,
} from 'react-icons/fa';
import { integrationsApi } from '../../services/integrationsService';
import { chartDataLabels } from '../../utils/chartDataLabels';
import './TrafficPage.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ArcElement, Tooltip, Legend, Filler);

const numFmt = new Intl.NumberFormat('es-MX');
const compact = (n) => new Intl.NumberFormat('es-MX', { notation: 'compact', maximumFractionDigits: 1 }).format(n || 0);
const pct = (n) => `${((n || 0) * 100).toFixed(1)}%`;
const dur = (secs) => {
  const s = Math.round(secs || 0);
  const m = Math.floor(s / 60);
  return `${m}m ${String(s % 60).padStart(2, '0')}s`;
};
const CHANNEL_COLORS = ['#073C5C', '#F68D2E', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#94a3b8'];
const fmtDay = (d) => (d && d.length === 8 ? `${d.slice(6, 8)}/${d.slice(4, 6)}` : d);

// ── Rango de fechas (presets + personalizado) ────────────────────────────────
const RANGE_PRESETS = [
  { id: '7d', label: '7 días' },
  { id: '28d', label: '28 días' },
  { id: '90d', label: '90 días' },
  { id: '365d', label: '12 meses' },
];
const RANGE_DAYS = { '7d': 7, '28d': 28, '90d': 90, '365d': 365 };
const ymd = (d) => {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};
// {from,to} en 'YYYY-MM-DD'. Para 'custom' incompleto, devuelve null en el hueco.
function resolveRange(range) {
  if (range.preset === 'custom') return { from: range.from || null, to: range.to || null };
  const days = RANGE_DAYS[range.preset] || 28;
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - days);
  return { from: ymd(from), to: ymd(to) };
}
function rangeLabel(range) {
  if (range.preset === 'custom') {
    return range.from && range.to ? `${range.from} – ${range.to}` : 'rango personalizado';
  }
  const p = RANGE_PRESETS.find((x) => x.id === range.preset);
  return p ? `últimos ${p.label}` : 'últimos 28 días';
}

export default function TrafficPage() {
  const { user } = useAuthenticator((context) => [context.user]);
  const username = user?.username;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notConnected, setNotConnected] = useState(false);
  const [data, setData] = useState(null);
  const [range, setRange] = useState({ preset: '28d', from: '', to: '' });
  // Vive aquí y no en la tarjeta: TrafficContent se desmonta en cada recarga y la
  // métrica elegida se perdería al pulsar "Actualizar" o cambiar de preset.
  const [monthMetric, setMonthMetric] = useState('totalUsers');

  const { from, to } = resolveRange(range);

  const load = useCallback(async () => {
    if (!username) return;
    // Un rango 'custom' incompleto no dispara recarga: se conserva la vista actual
    // hasta que estén ambas fechas.
    if (range.preset === 'custom' && (!from || !to)) return;
    setLoading(true); setError(null); setNotConnected(false);
    try {
      const res = await integrationsApi.gaTraffic(username, from, to);
      setData(res);
    } catch (e) {
      if (e.status === 400 || e.status === 404) setNotConnected(true);
      else setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [username, from, to, range.preset]);

  useEffect(() => { load(); }, [load]);

  if (notConnected) {
    return (
      <main className="trf-page">
        <div className="trf-state">
          <FaPlug className="big" />
          <h3>Google Analytics no está conectado</h3>
          <p>Un administrador debe conectar GA4 en la pantalla de Integraciones.</p>
          <button className="trf-btn primary" onClick={() => navigate('/integrations')}>Ir a Integraciones</button>
        </div>
      </main>
    );
  }
  if (error) {
    return (
      <main className="trf-page">
        <div className="trf-state">
          <FaExclamationTriangle className="big" />
          <h3>No se pudo cargar el tráfico</h3>
          <p>{error}</p>
          <button className="trf-btn" onClick={load}><FaSync /> Reintentar</button>
        </div>
      </main>
    );
  }

  return (
    <main className="trf-page">
      <header className="trf-header">
        <div className="trf-header-left">
          <span className="trf-title-icon"><FaGlobe /></span>
          <div>
            <h1 className="trf-title">Tráfico Web</h1>
            <div className="trf-subtitle">Google Analytics 4 · {rangeLabel(range)}</div>
          </div>
        </div>
        <div className="trf-header-actions">
          <DateFilter range={range} onChange={setRange} />
          <span className="trf-pill green">● En vivo · GA4</span>
          <button className="trf-btn" onClick={load} disabled={loading}><FaSync /> Actualizar</button>
        </div>
      </header>

      {loading ? (
        <div className="trf-state"><FaSpinner className="trf-spin big" /><p>Cargando tráfico web…</p></div>
      ) : (
        <TrafficContent data={data} monthMetric={monthMetric} onMonthMetric={setMonthMetric} />
      )}
    </main>
  );
}

// ── Filtro de rango de fechas (presets + personalizado) ──────────────────────
function DateFilter({ range, onChange }) {
  const custom = range.preset === 'custom';
  return (
    <div className="trf-datefilter">
      {RANGE_PRESETS.map((p) => (
        <button key={p.id} type="button" className={`trf-chip ${range.preset === p.id ? 'active' : ''}`}
          onClick={() => onChange({ preset: p.id, from: '', to: '' })}>{p.label}</button>
      ))}
      <button type="button" className={`trf-chip ${custom ? 'active' : ''}`}
        onClick={() => onChange({ preset: 'custom', from: range.from || '', to: range.to || '' })}>
        <FaRegCalendarAlt /> Personalizado
      </button>
      {custom && (
        <span className="trf-daterange-inputs">
          <input type="date" value={range.from || ''} max={range.to || undefined}
            onChange={(e) => onChange({ preset: 'custom', from: e.target.value, to: range.to || '' })} />
          <span className="trf-dash">–</span>
          <input type="date" value={range.to || ''} min={range.from || undefined}
            onChange={(e) => onChange({ preset: 'custom', from: range.from || '', to: e.target.value })} />
        </span>
      )}
    </div>
  );
}

// ── Contenido (KPIs + gráficas). Todo se deriva del payload GA4 ya recalculado
//    por el backend para el rango seleccionado. ────────────────────────────────
function TrafficContent({ data, monthMetric, onMonthMetric }) {
  const k = data?.kpis || {};
  const ts = data?.timeseries || [];
  const channels = data?.channels || [];
  const pages = data?.pages || [];
  const devices = data?.devices || [];
  const countries = data?.countries || [];

  const lineData = {
    labels: ts.map((r) => fmtDay(r.date)),
    datasets: [{
      label: 'Sesiones', data: ts.map((r) => Number(r.sessions) || 0),
      borderColor: '#073C5C', backgroundColor: 'rgba(7,60,92,.12)', fill: true,
      tension: 0.35, pointRadius: 0, borderWidth: 2,
    }],
  };
  const lineOpts = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c) => `Sesiones: ${numFmt.format(c.parsed.y)}` } } },
    scales: { y: { beginAtZero: true, ticks: { callback: (v) => compact(v), font: { size: 10 }, maxTicksLimit: 5 }, grid: { color: '#f1f5f9' } }, x: { grid: { display: false }, ticks: { font: { size: 10 }, maxTicksLimit: 8 } } },
  };

  return (
    <>
      <section className="trf-kpis">
        <Kpi icon={<FaUsers />} accent="#073C5C" label="Usuarios" value={compact(k.totalUsers)} />
        <Kpi icon={<FaChartLine />} accent="#F68D2E" label="Sesiones" value={compact(k.sessions)} />
        <Kpi icon={<FaEye />} accent="#3b82f6" label="Vistas de página" value={compact(k.screenPageViews)} />
        <Kpi icon={<FaMousePointer />} accent="#10b981" label="Engagement" value={pct(k.engagementRate)} />
        <Kpi icon={<FaClock />} accent="#8b5cf6" label="Duración media" value={dur(k.averageSessionDuration)} />
        <Kpi icon={<FaBullseye />} accent="#ec4899" label="Conversiones" value={numFmt.format(Math.round(k.conversions || 0))} />
      </section>

      <div className="trf-grid">
        <MonthlyVisitsCard monthly={data?.monthly || []} metric={monthMetric} onMetric={onMonthMetric} />

        <DevicesCard devices={devices} comparison={data?.comparison} />
        <CountriesCard countries={countries} total={data?.countries_total} base={data?.countries_users} />
        <ChannelsCard channels={channels} />

        <section className="trf-card trf-col-8">
          <h2 className="trf-card-title"><FaChartLine /> Sesiones por día</h2>
          <div className="trf-chart"><Line data={lineData} options={lineOpts} /></div>
        </section>

        <section className="trf-card trf-col-4">
          <h2 className="trf-card-title"><FaEye /> Páginas más vistas</h2>
          <div className="trf-list">
            {pages.length === 0 ? <div className="trf-empty">Sin datos.</div> : pages.map((p, i) => (
              <div className="trf-row" key={i}>
                <span className="trf-row-name" title={p.pagePath}>{p.pagePath}</span>
                <span className="trf-row-val">{numFmt.format(Number(p.screenPageViews) || 0)}</span>
              </div>
            ))}
          </div>
        </section>
      </div>
    </>
  );
}

// ── V1 · "Visitas Mensuales" ─────────────────────────────────────────────────
// Serie de 12 meses naturales con etiqueta sobre cada punto, badge en el último y
// comparativo del año anterior punteado. NO depende del filtro de fechas de la
// página: el backend la calcula siempre sobre los últimos 12 meses (bloque
// `monthly` de fetch_ga_traffic).
const MONTH_METRICS = [
  { id: 'totalUsers', label: 'Usuarios' },
  { id: 'sessions', label: 'Sesiones' },
  { id: 'screenPageViews', label: 'Vistas de página' },
];
// 'YYYYMM' → 'Ago 2025'
const fmtMonth = (ym) => {
  if (!ym || ym.length !== 6) return ym || '';
  const d = new Date(Number(ym.slice(0, 4)), Number(ym.slice(4, 6)) - 1, 1);
  const s = d.toLocaleDateString('es-MX', { month: 'short', year: 'numeric' }).replace('.', '');
  return s.charAt(0).toUpperCase() + s.slice(1);
};
// 112345 → '112.3K' (formato del visual; Intl 'compact' es-MX devuelve '112 mil')
const kFmt = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return numFmt.format(Math.round(v));
};

function MonthlyVisitsCard({ monthly, metric, onMetric }) {
  if (!monthly.length) {
    return (
      <section className="trf-card trf-col-12">
        <h2 className="trf-card-title"><FaChartLine /> Visitas Mensuales</h2>
        <div className="trf-empty">Sin serie mensual disponible.</div>
      </section>
    );
  }
  const met = MONTH_METRICS.find((m) => m.id === metric) || MONTH_METRICS[0];
  // null = la propiedad no medía ese mes. Se conserva como hueco (spanGaps:false)
  // en vez de convertirlo en 0, que se leería como "no hubo tráfico".
  const cur = monthly.map((r) => (r[met.id] == null ? null : Number(r[met.id])));
  const prev = monthly.map((r) => (r[`${met.id}_prev`] == null ? null : Number(r[`${met.id}_prev`])));
  // El badge va sobre el último punto CON dato, no sobre el último del eje.
  const lastIdx = cur.reduce((acc, v, i) => (v == null ? acc : i), -1);
  const hasPrev = prev.some((v) => v != null);
  const partialCount = monthly.filter((r) => r.partial).length;
  const sinDato = cur.filter((v) => v == null).length;

  const chartData = {
    labels: monthly.map((r) => fmtMonth(r.month)),
    datasets: [
      {
        label: met.label, data: cur,
        borderColor: '#3b82f6', backgroundColor: 'rgba(59,130,246,.14)', fill: true,
        tension: 0.35, borderWidth: 3, pointRadius: 4, pointBackgroundColor: '#3b82f6',
        pointBorderColor: '#fff', pointBorderWidth: 2, pointHoverRadius: 6,
        spanGaps: false,
        // El último punto lleva el badge (se dibuja aparte), así que su etiqueta normal se omite.
        _label: (v) => kFmt(v), _labelPos: 'above', _labelColor: '#1e3a5f',
        _labelFont: '700 11px Roboto, system-ui, sans-serif',
        _labelSkip: (v, i) => i === lastIdx,
      },
      ...(hasPrev ? [{
        label: `${met.label} (año anterior)`, data: prev,
        borderColor: '#93c5fd', backgroundColor: 'transparent', fill: false,
        borderDash: [6, 4], tension: 0.35, borderWidth: 2, pointRadius: 0,
        spanGaps: false,
      }] : []),
    ],
  };
  const chartOpts = {
    responsive: true, maintainAspectRatio: false,
    layout: { padding: { top: 26, right: 30 } },
    plugins: {
      legend: { position: 'top', align: 'start', labels: { usePointStyle: true, boxWidth: 8, boxHeight: 8, padding: 14, font: { size: 11 } } },
      tooltip: {
        callbacks: {
          label: (c) => `${c.dataset.label}: ${c.parsed.y == null ? 'sin dato' : numFmt.format(c.parsed.y)}`,
          afterBody: (items) => (monthly[items[0].dataIndex]?.partial ? ['Mes en curso (incompleto)'] : []),
        },
      },
    },
    scales: {
      y: { beginAtZero: true, ticks: { callback: (v) => kFmt(v), font: { size: 10 }, maxTicksLimit: 6 }, grid: { color: '#f1f5f9' } },
      // offset:true mete el primer y el último punto dentro del área, para que su
      // etiqueta no se dibuje encima del eje Y ni se corte por la derecha.
      // autoSkip (default) evita que los 12 rótulos se encimen en pantallas chicas.
      x: { offset: true, grid: { display: false }, ticks: { font: { size: 10 }, maxRotation: 0, autoSkipPadding: 8 } },
    },
  };

  return (
    <section className="trf-card trf-col-12">
      <div className="trf-card-head">
        <h2 className="trf-card-title"><FaChartLine /> Visitas Mensuales</h2>
        <select className="trf-select" value={metric} onChange={(e) => onMetric(e.target.value)} aria-label="Métrica">
          {MONTH_METRICS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
        </select>
      </div>
      <div className="trf-chart tall">
        <Line data={chartData} options={chartOpts} plugins={[chartDataLabels, lastPointBadge]} />
      </div>
      <div className="trf-card-note">
        Últimos 12 meses · independiente del filtro de fechas.
        {partialCount > 0 && ' El mes en curso está incompleto.'}
        {sinDato > 0 && ` ${sinDato} mes(es) sin dato: esta propiedad de GA4 no devolvió filas para ese periodo.`}
        {!hasPrev && ' Sin comparativo del año anterior: esta propiedad de GA4 no devolvió datos para ese periodo.'}
        {hasPrev && prev.some((v) => v == null) && ' El comparativo del año anterior solo aparece en los meses con dato en la propiedad.'}
      </div>
    </section>
  );
}

// Badge azul con el valor del último punto de la primera serie (estilo del visual).
const lastPointBadge = {
  id: 'trfLastPointBadge',
  afterDatasetsDraw(chart) {
    const meta = chart.getDatasetMeta(0);
    if (!meta || meta.hidden || !meta.data?.length) return;
    // Último punto CON dato: si la serie termina en huecos (la propiedad todavía
    // no reportó el mes), el badge iría sobre un punto inexistente.
    const serie = chart.data.datasets[0].data;
    const idx = serie.reduce((acc, x, i) => (x == null ? acc : i), -1);
    if (idx < 0) return;
    const el = meta.data[idx];
    const v = serie[idx];
    if (!el || v == null || !Number.isFinite(v)) return;
    const txt = kFmt(v);
    const { ctx } = chart;
    ctx.save();
    ctx.font = '700 11px Roboto, system-ui, sans-serif';
    const w = ctx.measureText(txt).width + 16;
    const h = 22;
    // Se ancla al borde derecho del área si no cabe centrado sobre el punto.
    const x = Math.min(el.x, chart.chartArea.right - w / 2 - 2) - w / 2;
    const y = Math.max(el.y - h - 12, chart.chartArea.top);
    ctx.fillStyle = '#3b82f6';
    if (typeof ctx.roundRect === 'function') {
      ctx.beginPath(); ctx.roundRect(x, y, w, h, 11); ctx.fill();
    } else {
      ctx.fillRect(x, y, w, h);
    }
    ctx.fillStyle = '#fff';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(txt, x + w / 2, y + h / 2 + 0.5);
    ctx.restore();
  },
};

// ── V2a · Dispositivos ───────────────────────────────────────────────────────
const DEVICE_META = {
  mobile: { label: 'Mobile', icon: <FaMobileAlt /> },
  desktop: { label: 'Desktop', icon: <FaDesktop /> },
  tablet: { label: 'Tablet', icon: <FaTabletAlt /> },
};
const deviceInfo = (c) => DEVICE_META[(c || '').toLowerCase()] || { label: c || '—', icon: <FaMousePointer /> };

function DevicesCard({ devices, comparison }) {
  // El % y el total son contra la SUMA del desglose, no contra el total sin
  // dimensión: GA4 deduplica usuarios y ambos números no son comparables.
  const rows = devices.map((d) => ({ ...d, users: Number(d.totalUsers ?? d.sessions) || 0 }));
  const total = rows.reduce((a, r) => a + r.users, 0);
  const delta = comparison?.delta_pct;
  const cmpTitle = comparison
    ? `${comparison.range.since} – ${comparison.range.until} vs. ${comparison.prev_range.since} – ${comparison.prev_range.until} (solo días completos)`
    : undefined;

  return (
    <section className="trf-card trf-col-4 trf-card-flex">
      <h2 className="trf-card-title"><FaMousePointer /> Dispositivos</h2>
      <div className="trf-dev-list">
        {rows.length === 0 ? <div className="trf-empty">Sin datos.</div> : rows.map((d, i) => {
          const share = total ? d.users / total : 0;
          const info = deviceInfo(d.deviceCategory);
          return (
            <div className="trf-dev-row" key={i}>
              <span className="trf-dev-icon">{info.icon}</span>
              <div className="trf-dev-body">
                <div className="trf-dev-top">
                  <span className="trf-dev-name">{info.label}</span>
                  <span className="trf-dev-val">{numFmt.format(d.users)}</span>
                </div>
                <div className="trf-dev-sub">
                  <span className="trf-dev-pct">{(share * 100).toFixed(1)}%</span>
                </div>
                <div className="trf-bar-track"><div className="trf-bar-fill" style={{ width: `${share * 100}%` }} /></div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="trf-card-foot">
        <span className="trf-foot-icon blue"><FaUsers /></span>
        <div>
          <div
            className="trf-foot-label"
            title="Suma del desglose por dispositivo. No coincide con el KPI «Usuarios» de arriba porque GA4 deduplica el total de la propiedad: quien entra desde móvil y escritorio cuenta una vez en el KPI y dos veces aquí."
          >
            Usuarios por dispositivo (suma)
          </div>
          <div className="trf-foot-value">
            {numFmt.format(total)}
            {delta != null && (
              <span className={`trf-delta ${delta >= 0 ? 'up' : 'down'}`} title={cmpTitle}>
                {delta >= 0 ? <FaArrowUp /> : <FaArrowDown />} {Math.abs(delta * 100).toFixed(1)}%
              </span>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── V2b · Países ─────────────────────────────────────────────────────────────
// countryId es ISO-3166 alpha-2; se convierte a emoji de bandera con regional
// indicators. Si el sistema no tiene la fuente (típico en Windows), el navegador
// pinta las dos letras del país, que sigue siendo legible.
const flagEmoji = (iso2) => {
  const c = (iso2 || '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(c)) return '🌐';
  return String.fromCodePoint(...[...c].map((ch) => 0x1f1e6 + ch.charCodeAt(0) - 65));
};

function CountriesCard({ countries, total, base: baseProp }) {
  const rows = countries.map((c) => ({ ...c, users: Number(c.totalUsers ?? c.sessions) || 0 }));
  // Denominador = universo completo de la dimensión país que manda el backend
  // (`countries_users`), no la suma del top-8 ni el total de otra dimensión: si no,
  // los porcentajes se inflan al recortar la lista o comparan cosas distintas.
  const base = Number(baseProp) || rows.reduce((a, r) => a + r.users, 0);

  return (
    <section className="trf-card trf-col-4 trf-card-flex">
      <h2 className="trf-card-title"><FaGlobe /> Países</h2>
      <div className="trf-list">
        {rows.length === 0 ? <div className="trf-empty">Sin datos.</div> : rows.map((c, i) => (
          <div className="trf-row" key={i}>
            <span className="trf-flag" aria-hidden="true">{flagEmoji(c.countryId)}</span>
            <span className="trf-row-name">{c.country}</span>
            <span className="trf-row-val">{numFmt.format(c.users)}</span>
            <span className="trf-pct-pill">{base ? ((c.users / base) * 100).toFixed(1) : '0.0'}%</span>
          </div>
        ))}
      </div>
      <div className="trf-card-foot">
        <span className="trf-foot-icon blue"><FaGlobe /></span>
        <div>
          <div className="trf-foot-label">Total de países</div>
          <div className="trf-foot-value">{numFmt.format(total ?? rows.length)}</div>
        </div>
      </div>
    </section>
  );
}

// ── V3 · Canales de adquisición ──────────────────────────────────────────────
function ChannelsCard({ channels }) {
  const rows = channels.map((c) => ({
    name: c.sessionDefaultChannelGroup || '—',
    value: Number(c.sessions) || 0,
  })).sort((a, b) => b.value - a.value);
  const total = rows.reduce((a, r) => a + r.value, 0);
  const top = rows[0];

  const donutData = {
    labels: rows.map((r) => r.name),
    datasets: [{
      data: rows.map((r) => r.value), backgroundColor: CHANNEL_COLORS,
      borderColor: '#fff', borderWidth: 2,
      _label: (v) => (total && v / total >= 0.015 ? `${((v / total) * 100).toFixed(1)}%` : ''),
      _labelPos: 'insideArc', _labelColor: '#fff',
      _labelFont: '700 11px Roboto, system-ui, sans-serif',
    }],
  };
  const donutOpts = {
    responsive: true, maintainAspectRatio: false, cutout: '62%',
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (c) => `${c.label}: ${numFmt.format(c.parsed)} (${total ? ((c.parsed / total) * 100).toFixed(1) : '0.0'}%)` } },
    },
  };

  return (
    <section className="trf-card trf-col-4 trf-card-flex">
      <h2 className="trf-card-title"><FaGlobe /> Canales de adquisición</h2>
      {rows.length === 0 ? <div className="trf-empty">Sin datos.</div> : (
        <>
          <div className="trf-donut-wrap">
            <div className="trf-donut"><Doughnut data={donutData} options={donutOpts} plugins={[chartDataLabels]} /></div>
            <div className="trf-donut-legend">
              {rows.map((r, i) => (
                <div className="trf-leg" key={r.name}>
                  <span className="trf-leg-dot" style={{ background: CHANNEL_COLORS[i % CHANNEL_COLORS.length] }} />
                  <span className="trf-leg-name" title={r.name}>{r.name}</span>
                  <span className="trf-leg-pct">{total ? ((r.value / total) * 100).toFixed(1) : '0.0'}%</span>
                </div>
              ))}
            </div>
          </div>
          <div className="trf-card-foot two">
            <span className="trf-foot-icon blue"><FaChartBar /></span>
            <div>
              <div className="trf-foot-label">Total de sesiones</div>
              <div className="trf-foot-value">{numFmt.format(total)}</div>
            </div>
            <div className="trf-foot-sep" />
            <div>
              <div className="trf-foot-label">Canal principal</div>
              <div className="trf-foot-value sm">
                {top?.name || '—'}
                {top && <span className="trf-foot-hint"> ({total ? ((top.value / total) * 100).toFixed(1) : '0.0'}%)</span>}
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

function Kpi({ icon, accent, label, value }) {
  return (
    <div className="trf-kpi" style={{ '--trf-accent': accent }}>
      <div className="trf-kpi-head"><span className="trf-kpi-label">{label}</span><span className="trf-kpi-icon">{icon}</span></div>
      <div className="trf-kpi-value">{value}</div>
    </div>
  );
}
